import { useCustomTokens } from '@sushiswap/hooks'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Amount } from 'sushi'
import {
  type EvmToken,
  Position,
  type SushiSwapV3ChainId,
  type SushiSwapV3Pool,
} from 'sushi/evm'
import type { Address } from 'viem'
import { useConfig } from 'wagmi'
import { useMultiChainPrices } from '~evm/_common/ui/price-provider/price-provider/use-multi-chain-prices'
import { getConcentratedLiquidityPools } from '../../pools/actions/getConcentratedLiquidityPool'
import {
  getTokenWithCacheQueryFn,
  getTokenWithQueryCacheHydrate,
} from '../../tokens/useTokenWithCache'
import { getConcentratedLiquidityPositions } from '../actions/getConcentratedLiquidityPositions'
import { getStakedConcentratedLiquidityPositions } from '../actions/getStakedConcentratedLiquidityPositions'
import type { ConcentratedLiquidityPosition } from '../types'

interface UseConcentratedLiquidityPositionsData
  extends Omit<ConcentratedLiquidityPosition, 'token0' | 'token1'> {
  token0: EvmToken
  token1: EvmToken
  pool: SushiSwapV3Pool
  position: {
    position: Position
    positionUSD: number
    unclaimedUSD: number
  }
}

interface UseConcentratedLiquidityPositionsParams {
  account: Address | undefined
  chainIds: readonly SushiSwapV3ChainId[]
  enabled?: boolean
}

const getPoolKey = ({
  chainId,
  token0,
  token1,
  fee,
}: {
  chainId: SushiSwapV3ChainId
  token0: EvmToken
  token1: EvmToken
  fee: number
}) =>
  `${chainId}:${token0.address.toLowerCase()}:${token1.address.toLowerCase()}:${fee}`

export const useConcentratedLiquidityPositions = ({
  account,
  chainIds,
  enabled = true,
}: UseConcentratedLiquidityPositionsParams) => {
  const { data: customTokens, hasToken } = useCustomTokens()

  const {
    data: prices,
    isError: isPriceError,
    isLoading: isPriceInitialLoading,
  } = useMultiChainPrices({
    chainIds,
    enabled: Boolean(account),
  })

  const config = useConfig()

  const {
    data: positions,
    isError: isPositionsError,
    isLoading: isPositionsInitialLoading,
  } = useQuery({
    queryKey: [
      'useConcentratedLiquidityPositions',
      { chainIds, account, prices },
    ],
    queryFn: async () => {
      // Wallet-owned positions come from the on-chain position helper; staked
      // ones live in the SushiStaker contract, so they're invisible to
      // getConcentratedLiquidityPositions and must be fetched via the staker
      // subgraph. Merge both streams so the UI shows a unified list.
      const [walletPositions, stakedPositions] = await Promise.all([
        getConcentratedLiquidityPositions({ account, chainIds, config }),
        getStakedConcentratedLiquidityPositions({ account, chainIds, config }),
      ])

      // Dedup by tokenId: during subgraph indexing lag a position can
      // transiently appear in both lists (e.g. right after unstake, the NFT
      // is back on-chain but the staker subgraph hasn't removed it yet).
      // On-chain ownership is authoritative, so wallet entries win.
      const seenIds = new Set<bigint>()
      const positions = [...walletPositions, ...stakedPositions].filter((p) => {
        if (seenIds.has(p.tokenId)) return false
        seenIds.add(p.tokenId)
        return true
      })

      if (!positions.length) return []

      const positionsWithTokens = (
        await Promise.all(
          positions.map(async (position) => {
            const [token0Data, token1Data] = await Promise.all([
              getTokenWithCacheQueryFn({
                chainId: position.chainId,
                hasToken,
                customTokens,
                address: position.token0,
                config,
              }),
              getTokenWithCacheQueryFn({
                chainId: position.chainId,
                hasToken,
                customTokens,
                address: position.token1,
                config,
              }),
            ])

            return {
              ...position,
              token0: getTokenWithQueryCacheHydrate(
                position.chainId,
                token0Data,
              ),
              token1: getTokenWithQueryCacheHydrate(
                position.chainId,
                token1Data,
              ),
            }
          }),
        )
      ).filter((position) =>
        Boolean(position.token0 && position.token1),
      ) as (Omit<ConcentratedLiquidityPosition, 'token0' | 'token1'> & {
        token0: EvmToken
        token1: EvmToken
      })[]

      const poolKeys = new Map(
        positionsWithTokens.map(({ chainId, token0, token1, fee }) => [
          getPoolKey({ chainId, token0, token1, fee }),
          {
            chainId: chainId,
            token0: token0,
            token1: token1,
            feeAmount: fee,
          },
        ]),
      )

      const pools = new Map(
        (
          await getConcentratedLiquidityPools({
            poolKeys: Array.from(poolKeys.values()),
            config,
          })
        )
          .filter((pool): pool is SushiSwapV3Pool => Boolean(pool))
          .map((pool) => [getPoolKey(pool), pool]),
      )

      return positionsWithTokens
        .map((_position) => {
          const {
            chainId,
            token0,
            token1,
            fee,
            liquidity,
            tickLower,
            tickUpper,
            fees,
          } = _position
          const pool = pools.get(getPoolKey({ chainId, token0, token1, fee }))
          if (!pool) return undefined

          const position = new Position({
            pool,
            liquidity,
            tickLower,
            tickUpper,
          })

          const amountToUsd = (amount: Amount<EvmToken>) => {
            const _price = prices?.get(chainId)?.get(amount.currency.address)

            if (!amount?.gt(0n) || !_price) return 0
            const price = Number(
              Number(amount.toString()) * Number(_price.toFixed(10)),
            )
            if (Number.isNaN(price) || price < 0.000001) {
              return 0
            }

            return price
          }

          const positionUSD =
            amountToUsd(position.amount0) + amountToUsd(position.amount1)
          const unclaimedUSD =
            amountToUsd(new Amount(pool.token0, fees?.[0] || 0)) +
            amountToUsd(new Amount(pool.token1, fees?.[1] || 0))

          return {
            ..._position,
            pool,
            position: {
              position,
              positionUSD,
              unclaimedUSD,
            },
          }
        })
        .filter(
          (position): position is UseConcentratedLiquidityPositionsData =>
            typeof position !== 'undefined',
        )
    },
    refetchInterval: Number.POSITIVE_INFINITY,
    enabled: Boolean(
      account &&
        chainIds &&
        enabled &&
        (!isPriceInitialLoading || isPriceError),
    ),
  })

  return {
    data: positions,
    isError: isPositionsError || isPriceError,
    isInitialLoading: isPositionsInitialLoading || isPriceInitialLoading,
  }
}
